import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker';
import { Appearance, Design, Intent, Size, StretchMode } from '@Components/designConstants';
import { getFormattedValue } from '@Components/NumberFormatter/NumberFormatter';
import {
  ALLOW_ALL_DATES,
  ChannelColumn,
  DraftRow,
  formatLegacyDate,
  GoalRow,
  STANDARD_CALENDAR,
  WEEKS_AHEAD,
  WEEKS_BACK
} from './goal-intake-constants';
import {
  getChannelDrafts,
  getChannelWeeks,
  getChannelRange,
  weekEnd,
  layoutChannel
} from './goal-intake-edit-utils';
import { DATE_FORMAT, snapToMonday } from '../../utils/date-utils';
import { conditionallyPluralizeWithCount } from '../../utils/text-utils';
import { asNames, mixesTvadChannels } from './goal-intake-utils';
import {
  getLegacyTooltip,
  GoalValueField,
  LegacyMarker,
  MixedChannelMarker
} from './GoalIntakeColumns';

const shortDate = (date: string) => Dfns.format('MMM d')(Dfns.parseISO(date));
const range = (row: GoalRow) =>
  `${shortDate(row.start_date ?? '')} – ${shortDate(row.end_date ?? '')}`;
const money = (value?: number | null) =>
  getFormattedValue({ value: value ?? 0, type: '$', decimals: 0 });

interface GoalIntakeChannelViewProps {
  company: string;
  drafts: DraftRow[];
  unsavedKeys: Set<string>;
  errors: Map<string, string>;
  onChange: (key: string, row: GoalRow) => void;
  onDelete: (key: string) => void;
  // Adds a new draft and returns its key.
  onAdd: (row: GoalRow) => string;
}

const NO_ROWS = new Set<number>();
interface Selection {
  channel: string;
  from: number;
  to: number;
}

// Weeks down, channels across. A budget over whole weeks is one tall merged cell, a shorter one
// (or one that collides with another) is a badge in its week. Click a budget to edit it, click or
// drag over empty weeks to add one.
export const GoalIntakeChannelView = ({
  company,
  drafts,
  unsavedKeys,
  errors,
  onChange,
  onDelete,
  onAdd
}: GoalIntakeChannelViewProps) => {
  const today = Dfns.format(DATE_FORMAT, new Date());
  const byChannel = useMemo(() => getChannelDrafts(drafts), [drafts]);
  const { newest, oldest, open } = useMemo(
    () => getChannelRange(byChannel, today, WEEKS_AHEAD, WEEKS_BACK),
    [byChannel, today]
  );
  const scroller = useRef<HTMLDivElement>(null);
  const hasOpened = useRef(false);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const weeks = useMemo(() => getChannelWeeks(newest, oldest), [newest, oldest]);
  const channels = useMemo(() => [...byChannel.keys()], [byChannel]);
  const columns = useMemo(
    () =>
      new Map<string, ChannelColumn>(
        channels.map(channel => [channel, layoutChannel(byChannel.get(channel) ?? [], weeks)])
      ),
    [channels, byChannel, weeks]
  );
  const overlapCount = [...columns.values()].reduce((sum, col) => sum + col.overlapKeys.size, 0);
  const editing = drafts.find(draft => draft.key === editingKey);

  useEffect(() => {
    // Open on the latest budget, under the sticky header, once there is something to show.
    const el = scroller.current;
    const week = el?.querySelector<HTMLElement>(`[data-week='${snapToMonday(open)}']`);
    if (!el || !week || hasOpened.current) {
      return;
    }
    hasOpened.current = true;
    el.scrollTop =
      week.offsetTop -
      (el.querySelector<HTMLElement>('.goal-intake-channel-head')?.offsetHeight ?? 0);
  }, [open, weeks]);

  useEffect(() => {
    // A drag released outside an empty cell adds nothing.
    const finish = () => {
      setIsDragging(false);
      setSelection(null);
    };
    window.addEventListener('mouseup', finish);
    return () => window.removeEventListener('mouseup', finish);
  }, []);

  const addBudget = (channel: string, from: number, to: number) => {
    const newerRow = Math.min(from, to);
    const olderRow = Math.max(from, to);
    const key = onAdd({
      company,
      goal_name: 'Budget',
      Channel: [channel],
      start_date: weeks[olderRow],
      end_date: weekEnd(weeks[newerRow])
    });
    setEditingKey(key);
  };

  const occupiedByChannel = useMemo(
    () =>
      new Map(
        [...columns].map(([channel, column]) => [
          channel,
          new Set(
            column.blocks.flatMap(block =>
              Array.from({ length: block.span }, (_, i) => block.startRow + i)
            )
          )
        ])
      ),
    [columns]
  );

  const startSelect = (channel: string, row: number) => {
    setSelection({ channel, from: row, to: row });
    setIsDragging(true);
  };

  // Drag stops at the first week that already belongs to a budget.
  const extendSelect = (channel: string, row: number) => {
    if (!isDragging || selection?.channel !== channel) {
      return;
    }
    const occupied = occupiedByChannel.get(channel) ?? NO_ROWS;
    const step = row > selection.from ? 1 : -1;
    for (let r = selection.from; r !== row + step; r += step) {
      if (occupied.has(r)) {
        return;
      }
    }
    setSelection({ ...selection, to: row });
  };

  const endSelect = (channel: string) => {
    if (selection?.channel === channel) {
      addBudget(channel, selection.from, selection.to);
    }
    setSelection(null);
    setIsDragging(false);
  };

  // The cells are memoized, so they call the latest handlers through stable wrappers.
  const latest = useRef({ startSelect, extendSelect, endSelect, addBudget });
  useEffect(() => {
    latest.current = { startSelect, extendSelect, endSelect, addBudget };
  });
  const onStartSelect = useCallback(
    (channel: string, row: number) => latest.current.startSelect(channel, row),
    []
  );
  const onExtendSelect = useCallback(
    (channel: string, row: number) => latest.current.extendSelect(channel, row),
    []
  );
  const onEndSelect = useCallback((channel: string) => latest.current.endSelect(channel), []);
  const onAddWeek = useCallback(
    (channel: string, row: number) => latest.current.addBudget(channel, row, row),
    []
  );

  const gridTemplate = {
    gridTemplateColumns: `120px repeat(${channels.length}, minmax(180px, 1fr))`
  };

  return (
    <div className='goal-intake-channel-view'>
      <div className='goal-intake-channel-toolbar'>
        <span className='goal-intake-channel-note'>
          Click a budget to edit it. Click or drag over empty weeks to add one.
          {overlapCount > 0 && (
            <span className='goal-intake-channel-overlap-chip'>
              {conditionallyPluralizeWithCount(overlapCount, 'overlapping budget')}
            </span>
          )}
        </span>
      </div>
      {channels.length === 0 && (
        <div className='goal-intake-channel-empty'>
          No channel budgets yet. Add one in the Month or Week view.
        </div>
      )}
      <div className='goal-intake-channel-scroll' ref={scroller}>
        <div className='goal-intake-channel-grid' style={gridTemplate}>
          <div className='goal-intake-channel-head'>Week</div>
          {channels.map(channel => (
            <div key={channel} className='goal-intake-channel-head'>
              {channel}
            </div>
          ))}
          {weeks.map((week, row) => (
            <div
              key={week}
              className={cn('goal-intake-channel-week', {
                current: week <= today && today <= weekEnd(week)
              })}
              style={{ gridRow: row + 2, gridColumn: 1 }}
              data-week={week}
            >
              <b>{shortDate(week)}</b>
            </div>
          ))}
          {channels.map((channel, col) => {
            const layout = columns.get(channel);
            const occupied = occupiedByChannel.get(channel) ?? NO_ROWS;
            return (
              <ChannelColumnCells
                key={channel}
                channel={channel}
                column={col + 2}
                weekCount={weeks.length}
                layout={layout}
                occupied={occupied}
                unsavedKeys={unsavedKeys}
                selection={selection?.channel === channel ? selection : null}
                onStartSelect={onStartSelect}
                onExtendSelect={onExtendSelect}
                onEndSelect={onEndSelect}
                onAddWeek={onAddWeek}
                onEdit={setEditingKey}
              />
            );
          })}
        </div>
      </div>
      {editing && (
        <BudgetEditor
          draft={editing}
          error={errors.get(editing.key)}
          onChange={row => onChange(editing.key, row)}
          onDelete={() => {
            onDelete(editing.key);
            setEditingKey(null);
          }}
          onHide={() => setEditingKey(null)}
        />
      )}
    </div>
  );
};

interface ChannelColumnCellsProps {
  channel: string;
  column: number;
  weekCount: number;
  layout?: ChannelColumn;
  occupied: Set<number>;
  unsavedKeys: Set<string>;
  selection: Selection | null;
  onStartSelect: (channel: string, row: number) => void;
  onExtendSelect: (channel: string, row: number) => void;
  onEndSelect: (channel: string) => void;
  onAddWeek: (channel: string, row: number) => void;
  onEdit: (key: string) => void;
}

const ChannelColumnCells = memo(
  ({
    channel,
    column,
    weekCount,
    layout,
    occupied,
    unsavedKeys,
    selection,
    onStartSelect,
    onExtendSelect,
    onEndSelect,
    onAddWeek,
    onEdit
  }: ChannelColumnCellsProps) => {
    // Badges sharing a week stack in one box instead of drawing on top of each other.
    const badgeRows = Object.entries(
      Object.groupBy(layout?.badges ?? [], badge => String(badge.row))
    );
    return (
      <>
        {Array.from({ length: weekCount }, (_, row) =>
          occupied.has(row) ? null : (
            <Button
              aria-label='Add budget'
              key={`${channel}-empty-${row}`}
              appearance={Appearance.EMPTY}
              design={Design.NEUTRAL}
              disableAnimation
              className={cn('goal-intake-channel-cell empty', {
                selecting:
                  selection !== null &&
                  row >= Math.min(selection.from, selection.to) &&
                  row <= Math.max(selection.from, selection.to)
              })}
              style={{ gridRow: row + 2, gridColumn: column }}
              data-testid='goal-intake-channel-empty'
              onMouseDown={() => onStartSelect(channel, row)}
              onMouseEnter={() => onExtendSelect(channel, row)}
              onMouseUp={() => onEndSelect(channel)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onAddWeek(channel, row);
                }
              }}
            />
          )
        )}
        {layout?.blocks.map(({ draft, startRow, span }) => (
          <Button
            key={`${channel}-${draft.key}`}
            appearance={Appearance.EMPTY}
            design={Design.NEUTRAL}
            disableAnimation
            className={cn('goal-intake-channel-cell block', {
              merged: span > 1,
              unsaved: unsavedKeys.has(draft.key),
              overlap: layout.overlapKeys.has(draft.key)
            })}
            style={{ gridRow: `${startRow + 2} / span ${span}`, gridColumn: column }}
            data-testid='goal-intake-channel-block'
            onClick={() => onEdit(draft.key)}
          >
            <div className='goal-intake-cell-content'>
              <b>{money(draft.row.goal_value)}</b>
              <span className='goal-intake-cell-range'>{range(draft.row)}</span>
            </div>
            {draft.row.legacy && <LegacyMarker row={draft.row} />}
          </Button>
        ))}
        {badgeRows.map(([row, badges = []]) => (
          <div
            key={`${channel}-badges-${row}`}
            className='goal-intake-channel-badges'
            style={{ gridRow: Number(row) + 2, gridColumn: column }}
          >
            {badges.map(({ draft }) => (
              <Button
                key={`${channel}-${draft.key}-badge-${row}`}
                size={Size.SM}
                appearance={Appearance.OUTLINED}
                design={layout?.overlapKeys.has(draft.key) ? Intent.ERROR : Intent.WARNING}
                disableAnimation
                className='goal-intake-channel-badge'
                data-testid='goal-intake-channel-badge'
                title={`${range(draft.row)}, ${money(draft.row.goal_value)}`}
                onClick={() => onEdit(draft.key)}
              >
                {mixesTvadChannels(draft.row) && <MixedChannelMarker />}
                {layout?.overlapKeys.has(draft.key) ? 'Overlap ' : ''}
                {range(draft.row)} {money(draft.row.goal_value)}
              </Button>
            ))}
          </div>
        ))}
      </>
    );
  }
);

interface BudgetEditorProps {
  draft: DraftRow;
  error?: string;
  onChange: (row: GoalRow) => void;
  onDelete: () => void;
  onHide: () => void;
}

// Edits one budget. An existing booking keeps its dates (only its amount can change).
const BudgetEditor = ({ draft, error, onChange, onDelete, onHide }: BudgetEditorProps) => {
  const { row } = draft;
  const dateField = (field: 'start_date' | 'end_date', label: string) => (
    <div className='goal-intake-channel-field'>
      <span>{label}</span>
      {row.legacy ? (
        <span>{formatLegacyDate(row[field] ?? '')}</span>
      ) : (
        <DatePicker
          isSingle
          numVisibleMonths={1}
          hideDateInput
          fiscalCalOverride={STANDARD_CALENDAR}
          size={Size.MD}
          stretch={StretchMode.FILL}
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          placeholder='mm/dd/yyyy'
          range={{ start: row[field] ?? '', end: row[field] ?? '' }}
          isOutsideRange={ALLOW_ALL_DATES}
          onChange={({ start }) => onChange({ ...row, [field]: start })}
        />
      )}
    </div>
  );
  return (
    <AlertDialog
      show
      onHide={onHide}
      intent={Intent.INFO}
      title={`${asNames(row.Channel).join(', ')} budget`}
      dataTestId='goal-intake-channel-editor'
      body={
        <div className='goal-intake-channel-editor'>
          {row.legacy && <p>{getLegacyTooltip(row)}</p>}
          {dateField('start_date', 'Start')}
          {dateField('end_date', 'End')}
          <div className='goal-intake-channel-field'>
            <span>Amount</span>
            <GoalValueField row={row} onChange={onChange} />
          </div>
          {error && <p className='goal-intake-row-error'>{error}</p>}
        </div>
      }
      tertiaryAction={{ label: 'Delete', design: Intent.ERROR, onClick: onDelete }}
      primaryAction={{ label: 'Done' }}
    />
  );
};
